import { registerPlugin } from '@capacitor/core';
import { SEConsentPurpose } from '@schmap/cordova-plugin-se-consent/www/SEConsentPlugin';
import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import ReactGA from 'react-ga4';
import { GaOptions } from 'react-ga4/types/ga4';
import { v4 as uuid } from 'uuid';

import { APP_VERSION, ATT_STATUS_STORAGE_KEY, IS_IOS, IS_NATIVE } from '../../common/constants';
import {
  ANALYTICS_CONSENT_PURPOSE_ID,
  instantiateConsentManagement,
} from '../../features/account/settings/consent-management';
import type { ConsentEventSource } from '../native/dynatrace-events';
import { trackDynatraceAnalyticsConsentConfirmed } from '../native/dynatrace-events';

interface DynatracePrivacyPlugin {
  setTrackingEnabled(options: { enabled: boolean }): Promise<void>;
}

const DynatracePrivacy = registerPlugin<DynatracePrivacyPlugin>('DynatracePrivacy');

const PLUGIN_TIMEOUT_MS = 3000;
const GA_CLIENT_ID_STORAGE_KEY = 'ga:clientId';
const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;

let analyticsUserId: string | null = null;
let isAnalyticsTrackingEnabled = false;
let isGoogleAnalyticsInitialized = false;
let pendingTrackingEnabledCallback: (() => void) | undefined;
let latestConsentAttemptId = 0;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error(`Plugin call timed out after ${ms}ms`)), ms)),
  ]);
}

function getPendingAttStatus() {
  return sessionStorage.getItem(ATT_STATUS_STORAGE_KEY);
}

export async function resolveTrackingConsent(
  onLateAuthorization?: () => void,
  userId?: number,
  consentPurposes?: SEConsentPurpose[],
  deferUntilUserAvailable = false,
  role?: string,
  onTrackingApplied?: () => void,
  onTrackingDenied?: () => void
): Promise<boolean> {
  const consentAttemptId = ++latestConsentAttemptId;
  if (userId !== undefined) analyticsUserId = userId.toString();

  if (role === 'SUPER_ADMIN') {
    pendingTrackingEnabledCallback = undefined;
    setGoogleAnalyticsTracking(false);
    setDynatraceTracking(false);
    onTrackingDenied?.();
    return false;
  }

  if (IS_NATIVE && deferUntilUserAvailable && userId === undefined && consentPurposes === undefined) {
    let hasNotified = false;
    const notifyTrackingEnabled = () => {
      if (hasNotified) return;
      hasNotified = true;
      if (pendingTrackingEnabledCallback === notifyTrackingEnabled) pendingTrackingEnabledCallback = undefined;
      onLateAuthorization?.();
    };
    pendingTrackingEnabledCallback = notifyTrackingEnabled;
    setDynatraceTracking(false);
    try {
      await Auth.currentAuthenticatedUser();
    } catch {
      return false;
    }

    if (consentAttemptId !== latestConsentAttemptId) return false;

    if (!role || role === 'SUPER_ADMIN') {
      return false;
    }

    const isTrackingAllowed = await resolveTrackingConsent(
      notifyTrackingEnabled,
      userId,
      consentPurposes,
      false,
      role,
      onTrackingApplied,
      onTrackingDenied
    );

    if (!isTrackingAllowed && !hasNotified) {
      pendingTrackingEnabledCallback = notifyTrackingEnabled;
    }

    return isTrackingAllowed;
  }

  if (!IS_NATIVE) {
    setGoogleAnalyticsTracking(true);
    return true;
  }

  const consentSource: ConsentEventSource = consentPurposes !== undefined ? 'settings' : 'account_refresh';
  const purposes = consentPurposes ?? (await instantiateConsentManagement());
  if (consentAttemptId !== latestConsentAttemptId) return false;

  const hasAnalyticsConsent = purposes.some(
    (purpose) => purpose.id === ANALYTICS_CONSENT_PURPOSE_ID && purpose.isSelected === true
  );

  if (!hasAnalyticsConsent) {
    setGoogleAnalyticsTracking(false);
    setDynatraceTracking(false);
    onTrackingDenied?.();
    return false;
  }

  if (!IS_IOS) {
    setGoogleAnalyticsTracking(true);
    setDynatraceTracking(true, () => {
      if (consentAttemptId !== latestConsentAttemptId) return;
      trackDynatraceAnalyticsConsentConfirmed(consentSource);
      notifyPendingTrackingEnabledCallback();
      onTrackingApplied?.();
    });
    return true;
  }

  let attStatus = getPendingAttStatus();
  const hadPendingStatus = attStatus !== null;

  if (!attStatus) {
    const statusPromise = AppTrackingTransparency.getStatus();

    try {
      attStatus = (await withTimeout(statusPromise, PLUGIN_TIMEOUT_MS)).status;
    } catch (error) {
      console.error('Failed to read ATT status for analytics gating', error);
      attStatus = null;

      // Timeout path: the plugin is still pending. Keep listening — if it eventually
      // comes back authorized, enable tracking and notify the caller (e.g. to init Sentry).
      statusPromise
        .then(({ status }) => {
          if (consentAttemptId !== latestConsentAttemptId) return;
          if (status === 'denied' || status === 'restricted') onTrackingDenied?.();
          if (status !== 'authorized') return;
          setGoogleAnalyticsTracking(true);
          setDynatraceTracking(true, () => {
            if (consentAttemptId !== latestConsentAttemptId) return;
            trackDynatraceAnalyticsConsentConfirmed(consentSource);
            notifyPendingTrackingEnabledCallback(onLateAuthorization);
            onTrackingApplied?.();
          });
        })
        .catch(() => {
          // Plugin truly failed after timeout; nothing more to do.
        });
    }
  }

  if (consentAttemptId !== latestConsentAttemptId) return false;

  const isTrackingAllowed = attStatus === 'authorized';
  if (attStatus === 'denied' || attStatus === 'restricted') onTrackingDenied?.();
  setGoogleAnalyticsTracking(isTrackingAllowed);

  // Defer the native Dynatrace privacy call so it does not compete with WKWebView
  // initialization. The beacon flush triggered by setTrackingEnabled can cause TLS
  // retries that starve the WebContent process if called synchronously at boot.
  setDynatraceTracking(
    isTrackingAllowed,
    isTrackingAllowed
      ? () => {
          if (consentAttemptId !== latestConsentAttemptId) return;
          trackDynatraceAnalyticsConsentConfirmed(consentSource);
          notifyPendingTrackingEnabledCallback();
          onTrackingApplied?.();
        }
      : undefined
  );

  if (hadPendingStatus) {
    sessionStorage.removeItem(ATT_STATUS_STORAGE_KEY);
  }

  return isTrackingAllowed;
}

function notifyPendingTrackingEnabledCallback(onLateAuthorization?: () => void): void {
  const pendingCallback = pendingTrackingEnabledCallback;
  pendingTrackingEnabledCallback = undefined;
  pendingCallback?.();
  if (pendingCallback !== onLateAuthorization) onLateAuthorization?.();
}

type GtagWindow = Window & { gtag?: (...args: unknown[]) => void } & Record<string, unknown>;

function getGaMeasurementId(): string | undefined {
  return (
    viteEnv?.VITE_GA_MEASUREMENT_ID ?? (typeof process !== 'undefined' ? process.env.VITE_GA_MEASUREMENT_ID : undefined)
  );
}

// ReactGA.reset() leaves the loaded gtag.js running; `ga-disable-<id>` is Google's documented per-hit opt-out.
function setGoogleTagCollection(trackingId: string, enabled: boolean): void {
  const gtagWindow = window as unknown as GtagWindow;
  gtagWindow[`ga-disable-${trackingId}`] = !enabled;
  gtagWindow.gtag?.('consent', 'update', { analytics_storage: enabled ? 'granted' : 'denied' });
  if (!enabled) gtagWindow.gtag?.('config', trackingId, { user_id: null, send_page_view: false });
}

export function isGoogleAnalyticsTrackingEnabled(): boolean {
  return isAnalyticsTrackingEnabled && isGoogleAnalyticsInitialized;
}

function setGoogleAnalyticsTracking(enabled: boolean): void {
  if (enabled === isAnalyticsTrackingEnabled && enabled && isGoogleAnalyticsInitialized) return;

  isAnalyticsTrackingEnabled = enabled;
  const trackingId = getGaMeasurementId();
  if (!enabled) {
    if (trackingId) setGoogleTagCollection(trackingId, false);
    ReactGA.reset();
    isGoogleAnalyticsInitialized = false;
    return;
  }

  try {
    if (!trackingId || !analyticsUserId) return;

    let clientId = localStorage.getItem(GA_CLIENT_ID_STORAGE_KEY);
    if (!clientId) {
      // Generate random client ID, put it in local storage (should only run on first open of the app).
      clientId = uuid();
      localStorage.setItem(GA_CLIENT_ID_STORAGE_KEY, clientId);
    }

    const gaOptions: GaOptions = {
      userId: analyticsUserId,
      cookieDomain: 'none',
      clientId,
    };

    setGoogleTagCollection(trackingId, true);
    ReactGA.initialize(trackingId, { gaOptions });
    ReactGA.set({
      checkProtocolTask: null,
      user_properties: { app_version: APP_VERSION },
    });
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
    isGoogleAnalyticsInitialized = true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        category: 'feature',
        feature: 'user',
        slice: 'userSlice',
        method: 'setupGA',
      },
    });
    console.error('Failed to setup Google Analytics', error);
  }
}

/** Stops GA and removes every GA identifier from the device (logout / account deletion). */
export function clearGoogleAnalyticsIdentifiers(): void {
  analyticsUserId = null;
  setGoogleAnalyticsTracking(false);
  localStorage.removeItem(GA_CLIENT_ID_STORAGE_KEY);

  document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_'))
    .forEach((name) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
}

function setDynatraceTracking(enabled: boolean, onApplied?: () => void): void {
  setTimeout(async () => {
    try {
      await withTimeout(DynatracePrivacy.setTrackingEnabled({ enabled }), PLUGIN_TIMEOUT_MS);
      onApplied?.();
    } catch (error) {
      console.error('Failed to apply Dynatrace privacy settings', error);
    }
  }, 0);
}
